/* eslint-disable import/order */
import Vue from "vue";
import Router from "vue-router";

Vue.use(Router);
import routes from "./router";

if (!process.env || typeof process.env !== "object") {
  throw new Error("Environment is not available!");
}

const baseUrl = process.env.BASE_URL;
if (typeof baseUrl !== "string") {
  throw new Error("BASE_URL is not defined!");
}

const apiHost = process.env.VUE_APP_API_HOST;
if (typeof apiHost !== "string") {
  throw new Error("VUE_APP_API_HOST is not defined!");
}

const apiPath = process.env.VUE_APP_API_PATH;
if (typeof apiPath !== "string") {
  throw new Error("VUE_APP_API_PATH is not defined!");
}

const imageHandlerServiceUrl = process.env.VUE_APP_IMAGE_HANDLER_SERVICE_URL;
if (typeof imageHandlerServiceUrl !== "string") {
  throw new Error("VUE_APP_IMAGE_HANDLER_SERVICE_URL is not defined!");
}

const videoHandlerServiceUrl = process.env.VUE_APP_VIDEO_HANDLER_SERVICE_URL;
if (typeof videoHandlerServiceUrl !== "string") {
  throw new Error("VUE_APP_VIDEO_HANDLER_SERVICE_URL is not defined!");
}

const messageAttachmentHandlerServiceUrl =
  process.env.VUE_APP_MESSAGE_ATTACHMENT_HANDLER_SERVICE_URL;
if (typeof messageAttachmentHandlerServiceUrl !== "string") {
  throw new Error("VUE_APP_ATTACHMENT_HANDLER_SERVICE_URL is not defined!");
}

const router = new Router({
  mode: "history",
  base: baseUrl,
  routes,
});

(Vue as any).router = router;

const eventBus = new Vue();

import IdGenerator from "@/lib/services/id-generator";
import ApiErrorParserService from "@/modules/api/api-error-parser.service";
import ApiResponseHandlerService from "@/modules/api/api-response-handler.service";
import ApiRequestParamsBuilderService from "@/modules/api/api-request-params-builder.service";
import ErrorConverterService from "@/modules/forms/error-converter.service.ts";

import ApiService from "@/modules/api/api.service";
import LoginRedirectResolverService from "@/modules/account/login-redirect-resolver.service";

import UserRepository from "@/modules/account/user.repository";
import UserInfoRepository from "@/modules/account/user-info.repository";
import AuthenticatedUser from "@/modules/account/authenticated-user.model";

const apiErrorParser = new ApiErrorParserService();

const apiResponseHandler = new ApiResponseHandlerService(apiErrorParser);
const apiRequestParamsBuilder = new ApiRequestParamsBuilderService();

import axios, { AxiosInstance } from "axios";
import VueAxios from "vue-axios";

const api = new ApiService(
  axios,
  apiResponseHandler,
  apiRequestParamsBuilder,
  apiHost,
  apiPath
);

import VueAuth from "@websanova/vue-auth";

const apiServer: AxiosInstance = api.getServer();

Vue.use(VueAxios, apiServer);
import UserFactory from "@/modules/account/user.factory";

const userFactory = new UserFactory();

Vue.use(VueAuth, {
  // auth:             require('@websanova/vue-auth/drivers/auth/bearer.js'),
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  auth: require("@/modules/account/bearer.driver.js"),
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  http: require("@websanova/vue-auth/drivers/http/axios.1.x.js"),
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  router: require("@websanova/vue-auth/drivers/router/vue-router.2.x.js"),
  rolesVar: "role",
  loginData: {
    url: apiPath + "authentication/token_login",
    method: "POST",
    redirect: false,
    fetchUser: true,
  },
  logoutData: {
    url: apiPath + "authentication/logout",
    method: "POST",
    redirect: "/account/login",
    makeRequest: false,
  },
  fetchData: {
    //url: process.env.VUE_APP_API_PATH! + "authentication/users/me",
    url: apiPath + "permissions",
    method: "GET",
    enabled: true,
  },
  refreshData: {
    url: apiPath + "authentication/refresh",
    method: "GET",
    enabled: false,
    interval: 30,
  },
  authRedirect: { path: "/account/login" },
  parseUserData: (data: any) => {
    const permissions: string[] = [];
    data.availableResources.forEach((item: any) => {
      permissions.push(item.name);
    });

    return new AuthenticatedUser(
      data.userId.toString(),
      data.metaRole.name,
      permissions
    );
  },
});

import AuthenticatedUserProvider from "./modules/account/authenticated-user-provider.service";

const authenticatedUserProvider = new AuthenticatedUserProvider(
  (Vue as any).auth
);

import ApiCollectionResponseParserService from "@/modules/api/api-collection-response-parser.service";
import ImageHandlerService from "@/modules/file-storage/image-handler.service";
import VideoHandlerService from "@/modules/file-storage/video-handler.service";

import BasicRepository from "@/modules/api/basic.repository";
import { DirectoryValue } from "@/modules/api/directory-value.model";
import DirectoryValueFactory from "@/modules/api/directory-value.factory";

import AttachmentFactory from "./modules/messaging/attachment.factory";
import Attachment from "./modules/messaging/attachment.model";
import AttachmentRepository from "./modules/messaging/attachment.repository";
import UserInfoFactory from "@/modules/account/user-info.factory";

import User from "@/modules/account/user.model";
import UserInfo from "@/modules/account/user-info.model";
import Plushie from "@/modules/plushie/plushie.model";
import PlushieFactory from "@/modules/plushie/plushie.factory";
import PlushieRepository from "@/modules/plushie/plushie.repository";
import OrderInformationFactory from "@/modules/plushie/order-information.factory";
import OrderInformation from "@/modules/plushie/order-information.model";
import OrderInformationRepository from "@/modules/plushie/order-information.repository";
import MessageAttachmentDownloaderService from "@/modules/file-storage/message-attachment-downloader.service";
import MessageAttachmentHandlerService from "@/modules/file-storage/message-attachment-handler.service";
import FileProcessingRepository from "@/modules/file-storage/file-processing.repository";
import FileStorageItemFactory from "@/modules/file-storage/item.factory";
import FileStorageItem from "@/modules/file-storage/item.model";
import Product from "@/modules/plushie/product.model";
import ProductFactory from "@/modules/plushie/product.factory";
import PlushieImageFactory from "@/modules/plushie/plushie-image.factory";
import PlushieImage from "@/modules/plushie/plushie-image.model";
import PlushieImageRepository from "@/modules/plushie/plushie-image.repository";
import PlushieStorageItemNameResolverService from "@/modules/plushie/plushie-storage-item-name-resolver.service";
import PlushieUpgradeRelation from "@/modules/plushie/plushie-upgrade-relation.model";
import PlushieUpgradeRelationFactory from "@/modules/plushie/plushie-upgrade-relation.factory";
import PlushieUpgradeRelationRepository from "@/modules/plushie/plushie-upgrade-relation.repository";
import QaAssetFactory from "./modules/quality-inspection/qa-asset.factory";
import QaAsset from "./modules/quality-inspection/qa-asset.model";
import QaAssetRepository from "./modules/quality-inspection/qa-asset.repository";
import Upgrade from "./modules/plushie/upgrade.model";
import UpgradeFactory from "@/modules/plushie/upgrade.factory";
import MessageFactory from "./modules/messaging/message.factory";
import Message from "./modules/messaging/message.model";
import MessageRepository from "./modules/messaging/message.repository";
import CustomerPreviewRepository from "./modules/customer-preview/customer-preview.repository";
import CustomerPreviewAvailabilityCheckerService from "./modules/customer-preview/customer-preview-availability-checker.service";

const idGenerator = new IdGenerator();
const imageHandlerService = new ImageHandlerService(imageHandlerServiceUrl);
const videoHandlerService = new VideoHandlerService(videoHandlerServiceUrl);
const messageAttachmentHandlerService = new MessageAttachmentHandlerService(
  messageAttachmentHandlerServiceUrl
);

const messageAttachmentDownloaderService = new MessageAttachmentDownloaderService(
  messageAttachmentHandlerService
);

const customerPreviewAvailabilityChecker = new CustomerPreviewAvailabilityCheckerService();

const directoryValueFactory = new DirectoryValueFactory();
const directoryValueResponseParser = new ApiCollectionResponseParserService<
  DirectoryValue
>();

const attachmentFactory = new AttachmentFactory();
const attachmentResponseParser = new ApiCollectionResponseParserService<
  Attachment
>();

const attachmentRepository = new AttachmentRepository(
  api,
  attachmentFactory,
  attachmentResponseParser
);

const countryRepository = new BasicRepository<DirectoryValue>(
  "countries",
  api,
  directoryValueFactory,
  directoryValueResponseParser
);

const customerPreviewRepository = new CustomerPreviewRepository(api);

const userInfoFactory = new UserInfoFactory();

const userResponseParser = new ApiCollectionResponseParserService<User>();

const userRepository = new UserRepository(
  api,
  userFactory,
  userResponseParser,
  apiErrorParser,
  (Vue as any).auth,
  eventBus
);

const userInfoResponseParser = new ApiCollectionResponseParserService<
  UserInfo
>();

const userInfoRepository = new UserInfoRepository(
  api,
  userInfoFactory,
  userInfoResponseParser
);

const fileStorageItemFactory = new FileStorageItemFactory();
const fileStorageItemResponseParser = new ApiCollectionResponseParserService<
  FileStorageItem
>();

const fileStorageItemRepository = new BasicRepository<FileStorageItem>(
  "file-storage/items",
  api,
  fileStorageItemFactory,
  fileStorageItemResponseParser
);

const fileProcessingRepository = new FileProcessingRepository(
  api,
  fileStorageItemFactory
);

const historyTypeRepository = new BasicRepository<DirectoryValue>(
  "history_types",
  api,
  directoryValueFactory,
  directoryValueResponseParser
);

const languageRepository = new BasicRepository<DirectoryValue>(
  "languages",
  api,
  directoryValueFactory,
  directoryValueResponseParser
);

const messageFactory = new MessageFactory();
const messageResponseParser = new ApiCollectionResponseParserService<Message>();

const messageRepository = new MessageRepository(
  api,
  messageFactory,
  messageResponseParser
);

const orderInformationFactory = new OrderInformationFactory();
const orderInformationResponseParser = new ApiCollectionResponseParserService<
  OrderInformation
>();

const orderInformationRepository = new OrderInformationRepository(
  api,
  orderInformationFactory,
  orderInformationResponseParser
);

const plushieFactory = new PlushieFactory();
const plushieResponseParser = new ApiCollectionResponseParserService<Plushie>();

const plushieRepository = new PlushieRepository(
  api,
  plushieFactory,
  plushieResponseParser
);

const plushieImageFactory = new PlushieImageFactory();
const plushieImageResponseParser = new ApiCollectionResponseParserService<
  PlushieImage
>();

const plushieImageRepository = new PlushieImageRepository(
  api,
  plushieImageFactory,
  plushieImageResponseParser
);

const plushieStorageItemNameResolverService = new PlushieStorageItemNameResolverService();

const plushieUpgradeRelationFactory = new PlushieUpgradeRelationFactory();
const plushieUpgradeRelationResponseParser = new ApiCollectionResponseParserService<
  PlushieUpgradeRelation
>();

const plushieUpgradeRelationRepository = new PlushieUpgradeRelationRepository(
  api,
  plushieUpgradeRelationFactory,
  plushieUpgradeRelationResponseParser
);

const productFactory = new ProductFactory();
const productResponseParser = new ApiCollectionResponseParserService<Product>();

const productRepository = new BasicRepository<Product>(
  "products",
  api,
  productFactory,
  productResponseParser
);

const qaAssetFactory = new QaAssetFactory();
const qaAssetResponseParser = new ApiCollectionResponseParserService<QaAsset>();

const qaAssetRepository = new QaAssetRepository(
  api,
  qaAssetFactory,
  qaAssetResponseParser
);

const storeRepository = new BasicRepository<DirectoryValue>(
  "stores",
  api,
  directoryValueFactory,
  directoryValueResponseParser
);

const upgradeFactory = new UpgradeFactory();

const upgradeResponseParser = new ApiCollectionResponseParserService<Upgrade>();

const upgradeRepository = new BasicRepository<Upgrade>(
  "plushie_upgrades",
  api,
  upgradeFactory,
  upgradeResponseParser
);

const errorConverterService = new ErrorConverterService();

const redirectResolver = new LoginRedirectResolverService();

export const DIContainer = {
  window: window,
  Router: router,
  EventBus: eventBus,
  IdGenerator: idGenerator,
  ImageHandlerService: imageHandlerService,
  ApiService: api,
  AuthenticatedUserProvider: authenticatedUserProvider,
  MessageAttachmentDownloaderService: messageAttachmentDownloaderService,
  MessageAttachmentHandlerService: messageAttachmentHandlerService,
  AttachmentRepository: attachmentRepository,
  CountryRepository: countryRepository,
  CustomerPreviewAvailabilityChecker: customerPreviewAvailabilityChecker,
  CustomerPreviewRepository: customerPreviewRepository,
  ErrorConverterService: errorConverterService,
  FileProcessingRepository: fileProcessingRepository,
  FileStorageItemRepository: fileStorageItemRepository,
  HistoryTypeRepository: historyTypeRepository,
  LanguageRepository: languageRepository,
  LoginRedirectResolverService: redirectResolver,
  MessageRepository: messageRepository,
  OrderInformationRepository: orderInformationRepository,
  PlushieImageRepository: plushieImageRepository,
  PlushieRepository: plushieRepository,
  PlushieStorageItemNameResolverService: plushieStorageItemNameResolverService,
  PlushieUpgradeRelationRepository: plushieUpgradeRelationRepository,
  ProductRepository: productRepository,
  QaAssetRepository: qaAssetRepository,
  StoreRepository: storeRepository,
  UpgradeRepository: upgradeRepository,
  UserRepository: userRepository,
  UserInfoRepository: userInfoRepository,
  VideoHandlerService: videoHandlerService,
};
